import { Button, Modal, pantryColors } from '@pantry/ui'
import React, { useEffect, useMemo, useState } from 'react'
import type {
  LoyaltyCouponAppliesToMode,
  LoyaltyCouponDiscountType,
  LoyaltyCouponDraft,
  LoyaltyCouponFixedTarget,
  LoyaltyCouponLimits,
  LoyaltyCouponProductRef,
  LoyaltyCouponWebCategoryRef,
} from '../../../../types/loyaltyCoupon'
import { DEFAULT_LOYALTY_COUPON_LIMITS } from '../../../../types/loyaltyCoupon'
import { useLoyaltyCouponsContext } from '../hooks/useLoyaltyCouponsContext'
import { CouponCustomerEligibility } from './CouponCustomerEligibility'
import { CouponDeleteConfirmDialog } from './CouponDeleteConfirmDialog'
import { CouponIconPicker } from './CouponIconPicker'
import { CouponModeAll } from './CouponModeAll'
import { CouponModeCategories } from './CouponModeCategories'
import { CouponModeProducts } from './CouponModeProducts'
import { CouponLimitsSection } from './limits/CouponLimitsSection'

const p = pantryColors

interface FormState {
  code: string
  name: string
  description: string
  icon: string
  appliesTo: LoyaltyCouponAppliesToMode
  webCategories: LoyaltyCouponWebCategoryRef[]
  products: LoyaltyCouponProductRef[]
  discountType: LoyaltyCouponDiscountType
  // Whole-cart vs per-item basis for a whole-store fixed discount. Only
  // interactive when `appliesTo === 'all'` AND `discountType === 'fixed'`;
  // forced to 'item' for categories/products, ignored for percent.
  fixedTarget: LoyaltyCouponFixedTarget
  value: string
  // Customer eligibility. `restrictToSpecificCustomers` is the "All vs
  // Specific" toggle; `emailRestrictions` holds the allow-list. On save an
  // "All" selection persists `[]` regardless of any leftover chips.
  restrictToSpecificCustomers: boolean
  emailRestrictions: string[]
  // The new Expiry block lives inside `limits.expiresAt`. The standalone
  // `dateExpires` form field was removed in favour of the limits section.
  limits: LoyaltyCouponLimits
}

// New coupons default to "Has expiry" ON with a seeded date 2 days from today
// @ 23:59. Flipping the toggle OFF in `CouponExpiryBlock` swaps `expiresAt` to
// the COUPON_UNEXPIRE_AT sentinel so the wire payload always has a concrete
// value.
const seedExpiryDatetime = (): string => {
  const d = new Date()
  d.setDate(d.getDate() + 2)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59`
}

const buildEmptyForm = (): FormState => ({
  code: '',
  name: '',
  description: '',
  icon: '',
  // The 3-tab Applies-to section opens on "All Products" for new coupons —
  // friction-free starting point, matches the design.
  appliesTo: 'all',
  webCategories: [],
  products: [],
  discountType: 'fixed',
  // New coupons default to a whole-cart fixed discount ("Cart total").
  fixedTarget: 'cart',
  value: '',
  // New coupons open on "All customers" with an empty allow-list.
  restrictToSpecificCustomers: false,
  emailRestrictions: [],
  limits: {
    ...DEFAULT_LOYALTY_COUPON_LIMITS,
    // Override the type-level default (`null` = no expiry). The Coupons
    // dialog opens with "Has expiry" ON, seeded to today + 2d @ 23:59. The
    // toggle in `CouponExpiryBlock` can flip this to the COUPON_UNEXPIRE_AT
    // sentinel when the merchant wants no expiry.
    expiresAt: seedExpiryDatetime(),
  },
})

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[600],
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[800],
  background: p.white,
  outline: 'none',
}

const codeInputStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}

const formatErrors = (form: FormState): string | null => {
  if (!form.code.trim()) return 'Coupon code is required.'
  // Coupon Name is an optional field — intentionally not validated here.
  const numeric = Number.parseFloat(form.value)
  if (!Number.isFinite(numeric) || numeric <= 0)
    return 'Value must be greater than 0.'
  if (form.discountType === 'percent' && numeric > 100)
    return 'Percentage discounts cannot exceed 100%.'
  // 'all' mode is always valid — storewide coverage with no selection required.
  // The 'products' rule is new under the 3-tab UI: empty `products[]` used to
  // mean "all" under the old encoding; that's now explicit `appliesTo === 'all'`,
  // so an empty Products tab is a real validation failure.
  if (form.appliesTo === 'categories' && form.webCategories.length === 0)
    return 'Select at least one category.'
  if (form.appliesTo === 'products' && form.products.length === 0)
    return 'Select at least one product.'
  // Customer eligibility: "Specific customers" needs at least one email.
  if (form.restrictToSpecificCustomers && form.emailRestrictions.length === 0)
    return 'Add at least one customer email, or choose All customers.'
  // Limits cross-field checks
  const { perPerson, total, expiresAt } = form.limits
  if (perPerson !== null && total !== null && perPerson > total) {
    return 'Max uses per member cannot exceed max total uses.'
  }
  if (expiresAt !== null) {
    const t = Date.parse(expiresAt)
    if (!Number.isFinite(t)) return 'Expiry date is invalid.'
  }
  return null
}

const humanizeFieldName = (key: string): string =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

// The coupon gateways reject with whatever `bs-core`'s response-error
// interceptor threw — a string (`error.response.data.message`) when the
// backend sends one, otherwise the *raw* validation-error object (DRF field
// errors, e.g. `{ location_ids: ["This field is required."] }`), which is not
// an `Error` instance. Without this, any field-level validation failure
// (ACD-15035) silently collapsed to the generic fallback message.
const formatCouponError = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const fieldErrors = Object.entries(error as Record<string, unknown>).filter(
      (entry): entry is [string, string[]] => Array.isArray(entry[1]),
    )
    if (fieldErrors.length > 0) {
      return fieldErrors
        .map(
          ([key, messages]) =>
            `${humanizeFieldName(key)}: ${messages.join(' ')}`,
        )
        .join(' ')
    }
  }
  return fallback
}

// 3-tab pill row for the Applies-to section. Matches the HTML's
// `.seg-pills.applies-mode` styling (docs/pantry/2026.05.27/breadstack-loyalty-pro.html
// lines 980-986) — the inner "All Categories" / "All Products" toggles from
// the HTML are intentionally NOT carried into React: the All Products tab
// already covers the storewide case, so per-tab toggles would be redundant.
const APPLIES_TO_TABS: ReadonlyArray<{
  value: LoyaltyCouponAppliesToMode
  label: string
}> = [
  { value: 'all', label: 'All Products' },
  { value: 'categories', label: 'Categories' },
  { value: 'products', label: 'Products' },
]

const ModeToggle: React.FC<{
  mode: LoyaltyCouponAppliesToMode
  onChange: (next: LoyaltyCouponAppliesToMode) => void
}> = ({ mode, onChange }) => (
  <div
    style={{
      display: 'inline-flex',
      gap: 4,
      padding: 3,
      background: p.gray[100],
      borderRadius: 8,
    }}
  >
    {APPLIES_TO_TABS.map(({ value, label }) => {
      const active = value === mode
      return (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          style={{
            padding: '6px 12px',
            fontSize: 12.5,
            fontWeight: 600,
            borderRadius: 6,
            border: 'none',
            background: active ? p.white : 'transparent',
            color: active ? p.gray[800] : p.gray[500],
            cursor: 'pointer',
            boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
          }}
        >
          {label}
        </button>
      )
    })}
  </div>
)

const TrashIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
)

export const CouponModal: React.FC = () => {
  const {
    modalOpen,
    editingCouponId,
    editingCoupon,
    isLoadingEditingCoupon,
    closeModal,
    saveCoupon,
    deleteCoupon,
    isSaving,
    isDeleting,
  } = useLoyaltyCouponsContext()

  const [form, setForm] = useState<FormState>(buildEmptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Two-step destructive flow: clicking Delete opens this confirm dialog; the
  // actual delete only fires after the user clicks the destructive button.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  // `isEditing` is now derived from the editing id (set the moment the user
  // clicks Edit) — NOT from the resolved `editingCoupon`, which arrives one
  // tick later when the detail query lands. This keeps the title / Save label
  // / Delete button stable across the "open → fetch → seed" transition.
  const isEditing = Boolean(editingCouponId)

  // Sync form state when the modal opens / target coupon changes.
  useEffect(() => {
    if (!modalOpen) return
    // Edit mode is gated on the detail query: don't seed Create defaults into
    // the form while we're still waiting for the GET to resolve, otherwise
    // we'd flash the empty form, then overwrite it once the detail lands.
    if (isEditing && !editingCoupon) return
    setSubmitError(null)
    setFormError(null)
    setConfirmDeleteOpen(false)
    if (editingCoupon) {
      setForm({
        code: editingCoupon.code,
        name: editingCoupon.name,
        description: editingCoupon.description,
        icon: editingCoupon.icon ?? '',
        appliesTo: editingCoupon.appliesTo,
        webCategories: editingCoupon.webCategories,
        products: editingCoupon.products,
        discountType: editingCoupon.discountType,
        // Seed from the coupon's stored basis so a `fixed_cart` coupon re-opens
        // on "Cart total"; the mapper resolved this from the wire discount_type.
        fixedTarget: editingCoupon.fixedTarget,
        value: String(editingCoupon.value ?? ''),
        // Derive the "All vs Specific" toggle from the persisted allow-list —
        // a non-empty `email_restrictions` means the coupon was restricted.
        restrictToSpecificCustomers:
          (editingCoupon.emailRestrictions?.length ?? 0) > 0,
        emailRestrictions: editingCoupon.emailRestrictions ?? [],
        // The mapper always fills `limits` from the coupon's wire shape (with
        // defaults for fields the backend doesn't yet return), so we can read
        // it directly without a fallback.
        limits: editingCoupon.limits,
      })
    } else {
      // New coupons default to "Has expiry" ON with a freshly-seeded date
      // (today + 2 days @ 23:59). `buildEmptyForm` re-computes the seed each
      // open so the date stays fresh even if the dialog has been mounted for
      // a while. The user can flip the "Has expiry" toggle OFF in the Expiry
      // block to fall back to the MAX_DATETIME sentinel.
      setForm(buildEmptyForm())
    }
  }, [modalOpen, isEditing, editingCoupon])

  // While the detail query is in-flight on Edit mode, fall back to a neutral
  // title — `editingCoupon?.name` is undefined until the GET lands.
  const title = isEditing
    ? editingCoupon?.name
      ? `Edit Coupon · ${editingCoupon.name}`
      : 'Edit Coupon'
    : 'Create New Coupon'
  const saveLabel = isEditing ? 'Save Changes' : 'Create Coupon'
  // Detail query is gated on `modalOpen` in the provider, so this flag only
  // flips during the brief open → fetch window. Used to swap the form body
  // for a spinner and to disable Save until inputs are seeded.
  const isDetailLoading = isEditing && isLoadingEditingCoupon && !editingCoupon

  const handleSubmit = async () => {
    const err = formatErrors(form)
    if (err) {
      setFormError(err)
      return
    }
    setFormError(null)
    setSubmitError(null)
    const draft: LoyaltyCouponDraft = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      icon: form.icon.trim(),
      appliesTo: form.appliesTo,
      webCategories: form.webCategories,
      products: form.products,
      discountType: form.discountType,
      fixedTarget: form.fixedTarget,
      value: Number.parseFloat(form.value),
      // Legacy field — kept in sync with `limits.expiresAt` so older callers
      // that still read `dateExpires` directly don't break.
      dateExpires: form.limits.expiresAt ?? undefined,
      limits: form.limits,
      // "All customers" always persists an empty allow-list, even if the user
      // added chips then switched back to All.
      emailRestrictions: form.restrictToSpecificCustomers
        ? form.emailRestrictions
        : [],
    }
    try {
      await saveCoupon(draft)
      closeModal()
    } catch (error) {
      setSubmitError(
        formatCouponError(error, 'Saving the coupon failed. Please try again.'),
      )
    }
  }

  const handleDelete = () => {
    if (!editingCoupon) return
    setSubmitError(null)
    setConfirmDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!editingCoupon) return
    setSubmitError(null)
    try {
      await deleteCoupon(editingCoupon.id)
      setConfirmDeleteOpen(false)
      closeModal()
    } catch (error) {
      setConfirmDeleteOpen(false)
      setSubmitError(
        formatCouponError(
          error,
          'Deleting the coupon failed. Please try again.',
        ),
      )
    }
  }

  const handleCancelDelete = () => {
    if (isDeleting) return
    setConfirmDeleteOpen(false)
  }

  const footer = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0, color: p.red[700], fontSize: 12 }}>
          {submitError ?? formError ?? ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isEditing && (
            <Button
              variant="danger-ghost"
              size="sm"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <TrashIcon />
                Delete
              </span>
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={closeModal}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSaving || isDetailLoading}
          >
            {isSaving ? 'Saving…' : saveLabel}
          </Button>
        </div>
      </div>
    ),
    [
      submitError,
      formError,
      isEditing,
      isDeleting,
      isSaving,
      isDetailLoading,
      saveLabel,
      // handleSubmit/handleDelete intentionally not memoised — they close over
      // `form`, and the footer rerenders alongside the body.
      handleSubmit,
      handleDelete,
      closeModal,
    ],
  )

  // The destructive coupon label prefers the customer-facing code (e.g.
  // `SOURDOUGH5`) and falls back to the display name only when there is no code.
  const destructiveLabel =
    editingCoupon?.code || editingCoupon?.name || 'this coupon'

  // The "Discount basis" (cart vs item) control is only interactive for a
  // whole-store fixed discount. It's disabled + forced to "Per item" for
  // categories/products, and disabled for percent (a % is basis-agnostic).
  const isBasisDisabled =
    form.appliesTo !== 'all' || form.discountType === 'percent'

  // Display-only: a whole-store percentage applies to the whole cart, so the
  // disabled select reads "Cart total". We do NOT mutate `form.fixedTarget` —
  // that preserves the merchant's Fixed choice (e.g. "Per item") when they
  // toggle Percentage → back to Fixed.
  const displayedBasis =
    form.appliesTo === 'all' && form.discountType === 'percent'
      ? 'cart'
      : form.fixedTarget

  return (
    <>
      <CouponDeleteConfirmDialog
        open={confirmDeleteOpen}
        couponLabel={destructiveLabel}
        isPending={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <Modal
        open={modalOpen}
        onClose={isSaving || isDeleting ? () => {} : closeModal}
        title={title}
        width={620}
        footer={footer}
        // Sit above the Loyalty page's sticky header (zIndex 50). The destructive
        // CouponDeleteConfirmDialog (zIndex 1300) still lands on top of this.
        zIndex={1200}
      >
        {/* Scrollable form body — the Limitations & Restrictions block makes the
            modal taller than the viewport on smaller screens, so we cap the
            inner column and let it scroll independently of the footer. The
            scrollbar matches the navbar's thin style (see
            `frontend/src/layouts/Admin/style.ts` → `childrenWrap`). */}
        <style>{`
          .coupon-modal-scroll {
            scrollbar-width: thin;
            scrollbar-color: #b4bfd9 transparent;
          }
          .coupon-modal-scroll::-webkit-scrollbar {
            width: 5px;
            height: 5px;
          }
          .coupon-modal-scroll::-webkit-scrollbar-track {
            border-radius: 10px;
            background-color: transparent;
          }
          .coupon-modal-scroll::-webkit-scrollbar-thumb {
            border-radius: 10px;
            background-color: #b4bfd9;
          }
          @keyframes coupon-modal-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          .coupon-modal-spinner {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            border: 3px solid ${p.gray[200]};
            border-top-color: ${p.gray[500]};
            animation: coupon-modal-spin 0.8s linear infinite;
          }
        `}</style>
        <div
          className="coupon-modal-scroll"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxHeight: 'calc(100vh - 220px)',
            overflowY: 'auto',
            // The Modal body has `padding: 24` on all sides — bleed past the
            // top + right padding so the scrollbar sits at the dialog's right
            // border and the scroll travel starts flush at the top. Re-pad
            // inside so the first row clears the title border and input
            // borders don't kiss the scrollbar.
            marginTop: -24,
            marginRight: -24,
            paddingTop: 24,
            paddingRight: 10,
          }}
        >
          {isDetailLoading ? (
            // Detail GET is in-flight after the user clicked Edit. Render a
            // centered spinner while we wait so the form doesn't briefly flash
            // create-defaults before being overwritten by server data.
            <div
              style={{
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div className="coupon-modal-spinner" />
            </div>
          ) : (
            <>
              <div>
                <label style={fieldLabelStyle}>Coupon Icon</label>
                <CouponIconPicker
                  value={form.icon}
                  onChange={(icon) => setForm((f) => ({ ...f, icon }))}
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: '0 0 40%' }}>
                  <label style={fieldLabelStyle}>Coupon code</label>
                  <input
                    style={codeInputStyle}
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. SOURDOUGH5"
                    value={form.code}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Coupon Name</label>
                  <input
                    style={inputStyle}
                    type="text"
                    placeholder="e.g. $5 Off Sourdough Loaf"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div>
                <label style={fieldLabelStyle}>Description</label>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="Brief description shown to members"
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                />
              </div>

              <div>
                <label style={fieldLabelStyle}>Applies to</label>
                <ModeToggle
                  mode={form.appliesTo}
                  onChange={(next) =>
                    setForm((f) => ({
                      ...f,
                      appliesTo: next,
                      // The Discount basis control is disabled + forced to
                      // "Per item" for categories/products — reset it here so a
                      // disabled control never submits a stale 'cart'.
                      fixedTarget: next === 'all' ? f.fixedTarget : 'item',
                    }))
                  }
                />
              </div>

              {form.appliesTo === 'all' && <CouponModeAll />}
              {form.appliesTo === 'categories' && (
                <CouponModeCategories
                  webCategories={form.webCategories}
                  onWebCategoriesChange={(webCategories) =>
                    setForm((f) => ({ ...f, webCategories }))
                  }
                />
              )}
              {form.appliesTo === 'products' && (
                <CouponModeProducts
                  products={form.products}
                  onProductsChange={(products) =>
                    setForm((f) => ({ ...f, products }))
                  }
                />
              )}

              <CouponCustomerEligibility
                restrictToSpecific={form.restrictToSpecificCustomers}
                emails={form.emailRestrictions}
                onModeChange={(specific) =>
                  setForm((f) => ({
                    ...f,
                    restrictToSpecificCustomers: specific,
                  }))
                }
                onEmailsChange={(emailRestrictions) =>
                  setForm((f) => ({ ...f, emailRestrictions }))
                }
              />

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Discount Type</label>
                  <select
                    style={inputStyle}
                    value={form.discountType}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        discountType: e.target
                          .value as LoyaltyCouponDiscountType,
                      }))
                    }
                  >
                    <option value="fixed">Fixed amount ($)</option>
                    <option value="percent">Percentage (%)</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Value</label>
                  <input
                    style={inputStyle}
                    type="number"
                    min={0}
                    step="0.5"
                    placeholder={form.discountType === 'percent' ? '15' : '5'}
                    value={form.value}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, value: e.target.value }))
                    }
                  />
                </div>
                {/* Discount basis: whole-cart vs per-item. Only interactive for
                    a whole-store fixed discount — disabled + forced to "Per
                    item" for categories/products, disabled for percent (a % is
                    identical per-item vs per-cart). */}
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="coupon-discount-basis"
                    style={fieldLabelStyle}
                  >
                    Discount basis
                  </label>
                  <select
                    id="coupon-discount-basis"
                    style={
                      isBasisDisabled
                        ? {
                            ...inputStyle,
                            background: p.gray[100],
                            color: p.gray[400],
                            cursor: 'not-allowed',
                          }
                        : inputStyle
                    }
                    disabled={isBasisDisabled}
                    value={displayedBasis}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        fixedTarget: e.target.value as LoyaltyCouponFixedTarget,
                      }))
                    }
                  >
                    <option value="cart">Cart total</option>
                    <option value="item">Per eligible item</option>
                  </select>
                </div>
              </div>

              <CouponLimitsSection
                limits={form.limits}
                onChange={(limits) => setForm((f) => ({ ...f, limits }))}
              />
            </>
          )}
        </div>
      </Modal>
    </>
  )
}
